<script setup lang="ts">import { ref as _ref, computed as _computed, toRef as _toRef } from 'vue';
const __props_auditKeyId = _toRef(__props, 'auditKeyId');

import { computed, ref, watch } from "vue";
import { CollapsibleRoot, CollapsibleContent, CollapsibleTrigger } from "reka-ui";

import { RsLoading } from "@/components";

import { useBrandStore } from "@/stores/brand-store";

import { auditService } from "@/services";
import { fromUtc } from "@/services/UtilitiesService";

import type { AuditRecord } from "@/models/AuditRecord";

withDefaults(defineProps<{ auditKeyId?: string }>(), {  })

const isLoading = ref(false);
const hasError = ref(false);
let record = _ref<AuditRecord | null>(null);
const parametersVisible = _ref<boolean>(true);
const deltasVisible = _ref<boolean>(true);

watch(
  (__props_auditKeyId),
  async (to) => {
    if (!to) {
      return;
    }

    const selectedClient = useBrandStore().selectedClient;
    if (selectedClient == null) return;

    try {
      isLoading.value = true;
      hasError.value = false;

      record.value = await auditService.GetAuditRecord(selectedClient, parseInt(to, 10));
    } catch (error) {
      hasError.value = true;
    } finally {
      isLoading.value = false;
    }
  },
  { immediate: true },
);

const keysUsed = _computed((): string => {
  if (!record.value) {
    return "No keys found";
  }

  const keys = Object.entries(record.value.auditHeader)
    .filter(([key, value]) => key.startsWith("key") && !!value)
    .map(([, value]) => value)
    .join(", ");

  if (keys.length === 0) {
    return "No keys found";
  }
  return keys;
});

const showParameters = computed(() => Object.keys(record.value?.parameters ?? {}).length > 0);
</script>
<template>
  <div v-if="auditKeyId" class="tw-absolute tw-inset-0 tw-z-50 tw-flex tw-w-full tw-flex-col">
    <div
      class="tw-relative tw-mx-3 tw-my-3 tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-y-2 tw-bg-gray-100 tw-px-2 tw-pb-0 tw-pt-2"
    >
      <RsLoading v-if="isLoading" id="audit-record-loading-id" message="Loading Audit Record" />
      <p v-else-if="hasError" class="tw-mt-2 tw-text-center tw-font-medium">Error getting Audit Record</p>
      <template v-else>
        <p>
          Keys used:
          <span class="tw-font-bold tw-text-black">{{ keysUsed.toUpperCase() }}</span>
        </p>
        <form v-if="record !== null" class="tw-min-h-0 tw-flex-1 tw-overflow-y-auto">
          <div class="tw-grid tw-grid-cols-2 tw-gap-2 tw-py-2">
            <div>
              <div class="tw-grid lg:tw-grid-cols-4 [&>*]:tw-min-w-0">
                <span class="tw-font-semibold">Changed on:</span>
                <p class="tw-break-words lg:tw-col-span-3">
                  {{ fromUtc(record.auditHeader.actionOn) }}
                </p>
              </div>
            </div>
            <div>
              <div class="tw-grid lg:tw-grid-cols-4 [&>*]:tw-min-w-0">
                <span class="tw-font-semibold">Action Taken:</span>
                <p class="tw-break-words lg:tw-col-span-3">
                  {{ record.auditHeader.actionTaken }}
                </p>
              </div>
            </div>
          </div>
          <div class="tw-mb-3 tw-grid tw-grid-cols-2 tw-gap-2 tw-py-2">
            <div>
              <div class="tw-grid lg:tw-grid-cols-4 [&>*]:tw-min-w-0">
                <span class="tw-font-semibold">User Name:</span>
                <p class="tw-break-words lg:tw-col-span-3">{{ record.auditHeader.actionBy }}</p>
              </div>
            </div>
            <div>
              <div class="tw-grid lg:tw-grid-cols-4 [&>*]:tw-min-w-0">
                <span class="tw-font-semibold">Outcome:</span>
                <p class="tw-break-words lg:tw-col-span-3">{{ record.auditHeader.outcome }}</p>
              </div>
            </div>
          </div>
          <div class="tw-mb-3 tw-grid tw-grid-cols-2 tw-gap-2 tw-py-2">
            <div>
              <div class="tw-grid lg:tw-grid-cols-4 [&>*]:tw-min-w-0">
                <span class="tw-font-semibold">IP Address:</span>
                <p class="tw-break-words lg:tw-col-span-3">{{ record.auditHeader.ipAddress }}</p>
              </div>
            </div>
          </div>
          <CollapsibleRoot v-if="showParameters" v-model:open="parametersVisible">
            <CollapsibleTrigger
              class="tw-mb-2 tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-4 tw-bg-gray-300 tw-px-1 tw-text-left"
            >
              <p class="tw-text-base tw-font-medium">Parameters</p>
              <div class="tw-p-3">
                <i aria-hidden="true" class="fas" :class="parametersVisible ? 'fa-chevron-up' : 'fa-chevron-down'" />
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent class="tw-grid tw-grid-cols-2 tw-px-2">
              <div v-for="(val, key) in record.parameters" :key="key">
                <div :id="`parameter-${key}`" class="tw-mb-1 tw-break-words">
                  {{ val }}
                </div>
              </div>
            </CollapsibleContent>
          </CollapsibleRoot>
          <CollapsibleRoot v-if="record.delta" v-model:open="deltasVisible">
            <CollapsibleTrigger
              class="tw-mb-2 tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-4 tw-bg-gray-300 tw-px-1 tw-text-left"
            >
              <p class="tw-text-base tw-font-medium">Deltas</p>
              <div class="tw-p-3">
                <i aria-hidden="true" class="fas" :class="deltasVisible ? 'fa-chevron-up' : 'fa-chevron-down'" />
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent as-child class="tw-px-2">
              <pre>{{ record.delta }}</pre>
            </CollapsibleContent>
          </CollapsibleRoot>
        </form>
      </template>
    </div>
  </div>
</template>
